import { type FileSaverOptions } from 'file-saver';

/**
 * Downloads data as a file using file-saver.
 * Handles browser quirks and provides a consistent download experience.
 *
 * A string is text content and is saved as a text file. To download a remote
 * file or a `data:` URL, pass a `URL` instead. For a URL on another origin,
 * file-saver first sends a synchronous HEAD request. If that fails (e.g. no
 * CORS headers), it opens the URL in a new tab instead of downloading it, and
 * the returned promise still resolves.
 *
 * @param data - The content to download (text, a Blob, or a URL to fetch)
 * @param filename - Optional filename for the download
 * @param options - Optional file-saver options
 */
export async function download(
  data: string | Blob | URL,
  filename?: string,
  options?: FileSaverOptions,
): Promise<void> {
  const fileSaver = await import('file-saver');

  // file-saver treats strings as URLs, so a URL is passed as its href and text is wrapped in a Blob
  if (data instanceof URL) {
    return fileSaver.default.saveAs(data.href, filename, options);
  }
  const blob = typeof data === 'string' ? new Blob([data], { type: 'text/plain;charset=utf-8' }) : data;

  return fileSaver.default.saveAs(blob, filename, options);
}

/** One text file inside a zip. */
export type ZipTextFile = { name: string; content: string };

/**
 * Zips text files and downloads the archive, matching the house pattern (jszip
 * + file-saver). `jszip` is imported lazily so it stays out of the initial
 * bundle — only the click that exports pays for it.
 */
export async function downloadZip(filename: string, files: ZipTextFile[]): Promise<void> {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  for (const file of files) {
    zip.file(file.name, file.content);
  }
  return download(await zip.generateAsync({ type: 'blob' }), filename);
}
